import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { busStudentList, busList } from "../../api/Transport";

import { SweetAlertShow } from "../master/Master";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { formatToDate } from "../master/FormatToDateTime";
import usePrivateLink from "../config/usePrivateLink";
import Master from "./Master";
import Pagination from "../components/Pagination";
import DownloadExcel from "../master/DownloadExcel";
import { PrintPage } from "../master/Print";
import { getBatchList } from "../../api/BatchService";

export default function BusStudentList() {
  const hasAccess = usePrivateLink();
  const [loading, setLoading] = useState(true);

  const [students, setStudents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [buses, setBuses] = useState([]);
  const [batches, setBatches] = useState([]);

  const [showFilters, setShowFilters] = useState(false);

  const [tempFilters, setTempFilters] = useState({
    batch_id: "",
    bus_id: "",
    payment_type: "",
    sort_by: "name",
    sort_order: "asc",
  });

  const [filters, setFilters] = useState({
    search: "",
    batch_id: "",
    bus_id: "",
    payment_type: "",
    sort_by: "name",
    sort_order: "asc",
    page: 1,
  });

  useEffect(() => {
    const parseSessions = JSON.parse(localStorage.getItem("sessions") ?? "[]");
    setSessions(parseSessions);
  }, []);

  useEffect(() => {
    const activeSession = sessions?.find((s) => Number(s.status) === 1)?.id;
    setFilters((prev) => {
      if (prev.session_id || !activeSession) return prev;
      return { ...prev, session_id: activeSession };
    });
  }, [sessions]);

  const getBusList = async () => {
    try {
      const response = await busList("limit=all");
      if (response?.data?.status) {
        setBuses(response?.data?.data || []);
      }
    } catch (error) {
      console.error("Bus list error:", error);
    }
  };

  const getBatchListData = async () => {
    try {
      const response = await getBatchList("limit=all");

      if (response?.data?.status) {
        setBatches(response?.data?.data || []);
      }
    } catch (error) {
      console.error("Batch list error:", error);
    }
  };

  const getBusStudents = async () => {
    setLoading(true);
    try {
      let addUrl = "";
      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (value) {
          addUrl += `&${key}=${encodeURIComponent(value)}`;
        }
      });
      const response = await busStudentList(addUrl);
      if (response?.data?.status) {
        setStudents(response?.data?.data);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load bus students.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message ??
        "Something went wrong!",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBusList();
    getBatchListData();
  }, []);

  useEffect(() => {
    getBusStudents();
  }, [filters]);

  const paymentBadge = (type) => {
    switch (type) {
      case "Monthly":
        return "bg-label-primary";

      case "Quarterly":
        return "bg-label-info";

      case "Half Year":
        return "bg-label-warning";

      case "Full Year":
        return "bg-label-success";

      default:
        return "bg-label-secondary";
    }
  };

  const TableSkeleton = () => {
    return (
      <>
        {Array.from({ length: 8 }).map((_, index) => (
          <tr key={index}>
            <td>
              <div className="placeholder-glow">
                <span className="placeholder col-8" />
              </div>
            </td>

            <td>
              <div className="placeholder-glow">
                <span className="placeholder col-10" />
                <span className="placeholder col-7 mt-1" />
              </div>
            </td>

            <td>
              <div className="placeholder-glow">
                <span className="placeholder col-8" />
                <span className="placeholder col-6 mt-1" />
              </div>
            </td>

            <td>
              <div className="placeholder-glow">
                <span className="placeholder col-8" />
              </div>
            </td>

            <td>
              <div className="placeholder-glow">
                <span className="placeholder col-7" />
              </div>
            </td>

            <td>
              <div className="placeholder-glow">
                <span className="placeholder col-8" />
              </div>
            </td>

            <td>
              <div className="placeholder-glow">
                <span className="placeholder col-7" />
                <span className="placeholder col-7 mt-1" />
              </div>
            </td>

            <td>
              <div className="placeholder-glow">
                <span className="placeholder col-6" />
              </div>
            </td>
          </tr>
        ))}
      </>
    );
  };

  const EmptyState = () => {
    return (
      <div className="text-center py-5">
        <div
          className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
          style={{
            width: 76,
            height: 76,
            background: "#f1f5f9",
          }}
        >
          <i className="ri-bus-line text-muted" style={{ fontSize: 36 }} />
        </div>

        <h6 className="fw-bold mb-1">No Bus Students Found</h6>

        <p className="text-muted mb-3">
          No student is enrolled with the selected filters.
        </p>

        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={clearFilters}
        >
          <i className="ri-refresh-line me-1" />
          Clear Filters
        </button>
      </div>
    );
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  const openFilters = () => {
    setTempFilters({
      batch_id: filters.batch_id,
      bus_id: filters.bus_id,
      payment_type: filters.payment_type,
      sort_by: filters.sort_by,
      sort_order: filters.sort_order,
    });

    setShowFilters(true);
  };

  const handleTempFilterChange = (key, value) => {
    setTempFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyFilters = () => {
    setFilters((prev) => ({
      ...prev,
      ...tempFilters,
      page: 1,
    }));

    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters((prev) => ({
      ...prev,
      search: "",
      batch_id: "",
      bus_id: "",
      payment_type: "",
      sort_by: "name",
      sort_order: "asc",
      page: 1,
    }));

    setTempFilters({
      batch_id: "",
      bus_id: "",
      payment_type: "",
      sort_by: "name",
      sort_order: "asc",
    });

    setShowFilters(false);
  };

  const handleDownload = async (type = "excel") => {
    try {
      const downloadData = [];
      let firstUrl = "";
      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (value !== undefined && value !== null && value !== "") {
          if (key !== "page") {
            firstUrl += `&${key}=${encodeURIComponent(value)}`;
          }
        }
      });

      const firstResponse = await busStudentList(`page=1${firstUrl}`);
      if (!firstResponse?.data?.status) {
        SweetAlertShow("info", "Warning", firstResponse?.data?.message);
        return;
      }

      const lastPage = firstResponse?.data?.data?.last_page || 1;

      const mapRows = (items) =>
        items.map((item) => ({
          "Student Name": item?.student?.name ?? "",
          "Registration No": item?.student?.registration_no ?? "",
          "Father Name": item?.student?.father_name ?? "",
          "Mother Name": item?.student?.mother_name ?? "",
          "Mobile No": item?.student?.phone ?? "",
          "Payment Type": item?.payment_type ?? "",
          "Joining Date": formatToDate(item?.joining_date) ?? "",
          "End Date": formatToDate(item?.end_date) ?? "",
          "Bus Number": item?.bus?.bus_number ?? "",
          "Driver Name": item?.bus?.driver_name ?? "",
          "Driver Number": item?.bus?.driver_number ?? "",
          "Stop Name": item?.stop?.stop_name ?? "",
          "Stop Fee": item?.stop?.stop_fee ?? "",
        }));

      downloadData.push(...mapRows(firstResponse?.data?.data?.data || []));

      for (let page = 2; page <= lastPage; page++) {
        const response = await busStudentList(`page=${page}${firstUrl}`);

        if (response?.data?.status) {
          downloadData.push(...mapRows(response?.data?.data?.data || []));
        }
      }

      if (downloadData.length) {
        if (type === "excel") {
          const now = new Date();
          const fileName = `Report_${now.getFullYear()}_${now.getMonth() + 1
            }_${now.getDate()}_${now.getHours()}_${now.getMinutes()}`;

          DownloadExcel(downloadData, [], fileName, []);
        } else {
          PrintPage(downloadData);
        }
      } else {
        SweetAlertShow("info", "Info", "No records found");
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getBatchStatus = (status) => {
    switch (Number(status)) {
      case 0:
        return {
          label: "New",
          className: "bg-label-info",
        };

      case 1:
        return {
          label: "Running",
          className: "bg-label-success",
        };

      case 2:
        return {
          label: "Finished",
          className: "bg-label-secondary",
        };

      default:
        return {
          label: "Unknown",
          className: "bg-label-secondary",
        };
    }
  };

  return (
    <>
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid mt-2">
                <div className="row py-5">
                  <Master />
                  <div className="col-12">
                    <div className="card border-0 shadow-sm mb-3">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="d-flex align-items-center justify-content-center rounded-3"
                              style={{
                                width: 52,
                                height: 52,
                                background:
                                  "linear-gradient(135deg,#185fa5,#4f8cff)",
                                color: "#fff",
                                fontSize: 25,
                                boxShadow: "0 5px 15px rgba(24,95,165,.2)",
                              }}
                            >
                              <i className="ri-bus-2-line" />
                            </div>

                            <div>
                              <h5 className="mb-1 fw-bold">Bus Student List</h5>

                              <div className="text-muted small">
                                <i className="ri-group-line me-1" />
                                {students?.total} Students
                              </div>
                            </div>
                          </div>

                          <div className="d-flex gap-2">
                            {hasAccess("/bus/export") && (
                              <button
                                type="button"
                                className="btn btn-outline-primary rounded-3 me-1"
                                onClick={() => handleDownload("excel")}
                              >
                                <i className="ri-file-excel-2-line me-2"></i>{" "}
                                Download
                              </button>
                            )}
                            {hasAccess("/bus/print") && (
                              <button
                                type="button"
                                className="btn btn-outline-info rounded-3"
                                onClick={() => handleDownload("print")}
                              >
                                <i className="ri-printer-line me-2"></i> Print
                              </button>
                            )}
                            {hasAccess("/bus/student/import") && (
                              <Link
                                className="btn btn-outline-info"
                                to="/bus/student/import"
                              >
                                <i className="ri-file-excel-2-line me-1" />
                                Student Import
                              </Link>
                            )}
                            {hasAccess("/bus/enroll/student") && (
                              <Link
                                className="btn btn-outline-primary"
                                to="/student/bus/enroll"
                              >
                                <i className="ri-user-add-line me-1" />
                                Enroll Student
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card border-0 shadow-sm mb-3">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          {/* Search */}
                          <div className="flex-grow-1">
                            <div className="input-group">
                              <span className="input-group-text">
                                <i className="ri-search-line" />
                              </span>

                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search name, registration no, phone..."
                                value={filters.search}
                                onChange={(e) =>
                                  handleFilterChange("search", e.target.value)
                                }
                              />
                            </div>
                          </div>

                          {/* Filter Button */}
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={openFilters}
                          >
                            <i className="ri-filter-3-line me-1" />
                            Filters
                            {(filters.batch_id ||
                              filters.bus_id ||
                              filters.payment_type ||
                              filters.sort_by !== "name" ||
                              filters.sort_order !== "asc") && (
                                <span className="badge bg-primary ms-2">
                                  {
                                    [
                                      filters.batch_id,
                                      filters.bus_id,
                                      filters.payment_type,
                                      filters.sort_by !== "name"
                                        ? filters.sort_by
                                        : "",
                                      filters.sort_order !== "asc"
                                        ? filters.sort_order
                                        : "",
                                    ].filter(Boolean).length
                                  }
                                </span>
                              )}
                          </button>

                          {/* Clear */}
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={clearFilters}
                            title="Clear Filters"
                          >
                            <i className="ri-filter-off-line me-1" />
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        <div className="table-responsive">
                          <table className="table table-hover mb-0">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Student</th>
                                <th>Parents</th>
                                <th>Mobile</th>
                                <th>Bus & Stop</th>
                                <th>Joining Date</th>
                                <th>Payment Type</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                <TableSkeleton />
                              ) : students.length === 0 ? (
                                <tr>
                                  <td colSpan="10" className="border-0">
                                    <EmptyState />
                                  </td>
                                </tr>
                              ) : (
                                students?.data?.map((item, index) => {
                                  return (
                                    <tr key={item.id}>
                                      <td>{students.from + index}</td>
                                      <td>
                                        <div>
                                          <div className="fw-semibold">
                                            {item.student?.name}
                                          </div>

                                          <small className="text-muted">
                                            {item.student?.registration_no}
                                          </small>
                                        </div>
                                      </td>
                                      <td>
                                        <div>
                                          <div>
                                            {item.student?.father_name || "-"}
                                          </div>

                                          <small className="text-muted">
                                            {item.student?.mother_name || "-"}
                                          </small>
                                        </div>
                                      </td>
                                      <td>
                                        <span className="badge rounded-pill bg-label-info">
                                          {item.student?.phone}
                                        </span>
                                      </td>
                                      <td>
                                        <div className="fw-semibold">
                                          {item.stop?.stop_name || "-"}
                                        </div>
                                        <div className="small text-muted">
                                          {item.bus?.bus_number || "-"}
                                          {"-"}
                                          {item.bus?.driver_name || "-"}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="fw-semibold">
                                          {formatToDate(item.joining_date)}
                                        </div>

                                        {item.end_date && (
                                          <div className="small text-muted">
                                            End: {formatToDate(item.end_date)}
                                          </div>
                                        )}
                                      </td>

                                      <td>
                                        <span
                                          className={`badge ${paymentBadge(
                                            item.payment_type,
                                          )}`}
                                        >
                                          <i className="ri-wallet-3-line me-1" />

                                          {item.payment_type || "-"}
                                        </span>
                                      </td>

                                      <td>
                                        <div className="d-flex gap-1">
                                          {hasAccess("/student/details") && (
                                            <Link
                                              to={`/student/details?id=${item.student_id}&tab=Bus`}
                                              className="btn btn-sm btn-outline-primary"
                                            >
                                              <i className="ri-eye-line me-2" />
                                            </Link>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                          <Pagination
                            data={students}
                            setFilters={setFilters}
                            filters={filters}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Filter Offcanvas */}
      <div
        className={`offcanvas offcanvas-end ${showFilters ? "show" : ""}`}
        tabIndex="-1"
        style={{
          visibility: showFilters ? "visible" : "hidden",
          width: "400px",
        }}
      >
        <div className="offcanvas-header border-bottom">
          <div>
            <h5 className="offcanvas-title fw-bold mb-1">
              <i className="ri-filter-3-line me-2 text-primary" />
              Filters
            </h5>

            <small className="text-muted">Refine the bus student list</small>
          </div>

          <button
            type="button"
            className="btn-close"
            onClick={() => setShowFilters(false)}
            aria-label="Close"
          />
        </div>

        <div className="offcanvas-body">
          {/* Batch */}
          <div className="mb-4">
            <label className="form-label fw-semibold">
              <i className="ri-group-line me-1" />
              Batch
            </label>

            <select
              className="form-select"
              value={tempFilters.batch_id}
              onChange={(e) =>
                handleTempFilterChange("batch_id", e.target.value)
              }
            >
              <option value="">All Batches</option>

              {batches?.map((batch) => {
                const status = getBatchStatus(batch.status);

                return (
                  <option key={batch.id} value={batch.id}>
                    {batch.title} - {status.label}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Bus */}
          <div className="mb-4">
            <label className="form-label fw-semibold">
              <i className="ri-bus-line me-1" />
              Bus
            </label>

            <select
              className="form-select"
              value={tempFilters.bus_id}
              onChange={(e) => handleTempFilterChange("bus_id", e.target.value)}
            >
              <option value="">All Buses</option>

              {buses?.map((bus) => (
                <option key={bus.id} value={bus.id}>
                  {bus.bus_number}
                </option>
              ))}
            </select>
          </div>

          {/* Payment Type */}
          <div className="mb-4">
            <label className="form-label fw-semibold">
              <i className="ri-wallet-3-line me-1" />
              Payment Type
            </label>

            <select
              className="form-select"
              value={tempFilters.payment_type}
              onChange={(e) =>
                handleTempFilterChange("payment_type", e.target.value)
              }
            >
              <option value="">All Payment Types</option>
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
              <option value="Half Year">Half Year</option>
              <option value="Full Year">Full Year</option>
            </select>
          </div>

          <hr className="my-4" />

          {/* Sorting */}
          <div className="mb-3">
            <label className="form-label fw-semibold">
              <i className="ri-sort-desc me-1" />
              Sorting
            </label>
          </div>

          {/* Sort By */}
          <div className="mb-4">
            <label className="form-label text-muted small">Sort By</label>

            <select
              className="form-select"
              value={tempFilters.sort_by}
              onChange={(e) =>
                handleTempFilterChange("sort_by", e.target.value)
              }
            >
              <option value="name">Student Name</option>
              <option value="registration_no">Registration Number</option>
            </select>
          </div>

          {/* Sort Order */}
          <div className="mb-4">
            <label className="form-label text-muted small">Sort Order</label>

            <select
              className="form-select"
              value={tempFilters.sort_order}
              onChange={(e) =>
                handleTempFilterChange("sort_order", e.target.value)
              }
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="offcanvas-footer border-top p-3">
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary flex-fill"
              onClick={() => {
                setTempFilters({
                  batch_id: "",
                  bus_id: "",
                  payment_type: "",
                  sort_by: "name",
                  sort_order: "asc",
                });
              }}
            >
              <i className="ri-refresh-line me-1" />
              Reset
            </button>

            <button
              type="button"
              className="btn btn-primary flex-fill"
              onClick={applyFilters}
            >
              <i className="ri-check-line me-1" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Offcanvas Backdrop */}
      {showFilters && (
        <div
          className="offcanvas-backdrop fade show"
          onClick={() => setShowFilters(false)}
        />
      )}
    </>
  );
}
